
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CLASSES } from '../constants';
import { ClassLevel } from '../types';
import SubjectSection from '../components/SubjectSection';
import { BookOpen, HelpCircle, Lightbulb, ChevronRight } from 'lucide-react';

const ClassPage: React.FC = () => {
  const { classId } = useParams<{ classId: string }>();
  const [classData, setClassData] = useState<ClassLevel | null>(null);

  useEffect(() => {
    const found = CLASSES.find(c => c.id === classId);
    if (found) setClassData(found);
    window.scrollTo(0, 0);
  }, [classId]);

  if (!classData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Class Not Found</h2>
          <Link to="/" className="text-blue-600 font-bold hover:underline">Return to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Page Header */}
      <div className="bg-slate-900 text-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center lg:text-left">
          <nav className="flex items-center space-x-2 text-sm text-slate-400 mb-6 justify-center lg:justify-start">
            <Link to="/" className="hover:text-blue-400 transition">Home</Link>
            <ChevronRight size={14} />
            <Link to="/notes-hub" className="hover:text-blue-400 transition">Notes Hub</Link>
            <ChevronRight size={14} />
            <span className="text-blue-400">{classData.name}</span>
          </nav>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6">{classData.name} Study Resources</h1>
          <p className="text-slate-300 text-lg max-w-3xl leading-relaxed">
            Everything you need to excel in your {classData.name} Board examinations. Curated by experts, tailored for success.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-8 space-y-16">
            <section className="bg-white p-8 md:p-12 rounded-3xl border border-gray-100 shadow-sm prose prose-slate max-w-none">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Introduction to {classData.name}</h2>
              <p className="text-slate-600 text-lg leading-relaxed">{classData.longIntro}</p>
              
              <div className="my-10 p-8 bg-blue-50 rounded-2xl border border-blue-100">
                <h3 className="text-2xl font-bold text-blue-900 mb-4">Syllabus Overview</h3>
                <p className="text-blue-800 leading-relaxed opacity-90">{classData.syllabusOverview}</p>
              </div>
            </section>

            <section>
              <div className="flex items-center justify-between mb-10">
                <h2 className="text-3xl font-bold text-slate-900">Subject-Wise Sample Notes</h2>
                <div className="hidden sm:block text-slate-500 font-medium">
                  {classData.subjects.length} Subjects Available
                </div>
              </div>
              <div className="space-y-8">
                {classData.subjects.map((subject) => (
                  <SubjectSection key={subject.id} subject={subject} />
                ))}
              </div>
            </section>

            {/* Study Tips Section */}
            <section className="bg-gradient-to-br from-amber-50 to-orange-50 p-8 md:p-12 rounded-3xl border border-amber-100 shadow-sm">
              <div className="flex items-center space-x-3 mb-8">
                <div className="bg-amber-500 p-2 rounded-lg">
                  <Lightbulb className="text-white h-6 w-6" />
                </div>
                <h2 className="text-3xl font-bold text-slate-900">Top Study Tips for {classData.name}</h2>
              </div>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {classData.studyTips.map((tip, idx) => (
                  <li key={idx} className="flex items-start space-x-4">
                    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-200 text-amber-800 flex items-center justify-center font-bold">{idx + 1}</span>
                    <p className="text-slate-700 font-medium leading-relaxed">{tip}</p>
                  </li>
                ))}
              </ul>
            </section>

            {/* Class Specific FAQs */}
            <section>
              <h2 className="text-3xl font-bold text-slate-900 mb-8 flex items-center">
                <HelpCircle className="mr-3 text-blue-600" />
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {classData.faqs.map((faq, idx) => (
                  <div key={idx} className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm">
                    <h4 className="text-lg font-bold text-slate-900 mb-2">{faq.question}</h4>
                    <p className="text-slate-600 leading-relaxed">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4 space-y-8">
            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm sticky top-28">
              <h3 className="text-xl font-bold text-slate-900 mb-6">Subject Navigation</h3>
              <ul className="space-y-3">
                {classData.subjects.map((sub) => (
                  <li key={sub.id}>
                    <a href={`#${sub.id}`} className="flex items-center justify-between p-3 rounded-xl hover:bg-blue-50 transition text-slate-600 hover:text-blue-600 group">
                      <span className="font-medium">{sub.name}</span>
                      <ChevronRight size={16} className="opacity-0 group-hover:opacity-100 transition" />
                    </a>
                  </li>
                ))}
              </ul>
              
              <div className="mt-10 pt-8 border-t border-gray-100 text-center">
                <p className="text-slate-500 text-sm mb-6">Can't find what you're looking for?</p>
                <Link to="/contact" className="block bg-slate-100 text-slate-900 py-3 rounded-xl font-bold hover:bg-slate-200 transition">Contact Support</Link>
              </div>
            </div>

            <div className="bg-blue-600 text-white p-8 rounded-3xl shadow-xl overflow-hidden relative">
              <div className="relative z-10">
                <h4 className="text-xl font-bold mb-3">New Notes Daily!</h4>
                <p className="text-blue-100 text-sm mb-6 leading-relaxed">We add new chapters and sample papers daily. Follow our updates.</p>
                <Link to="/notes-hub" className="inline-block bg-white text-blue-600 px-6 py-2 rounded-lg font-bold text-sm">Learn More</Link>
              </div>
              <div className="absolute -right-8 -bottom-8 opacity-20 transform rotate-12">
                <BookOpen size={140} />
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default ClassPage;
